import { useId, useEffect, useRef } from 'react';
import type { CheckboxProps, CheckboxSize } from './types';

// ─── Size class map ───────────────────────────────────────────────────────────

const SIZE_CLASSES: Record<CheckboxSize, { box: string; icon: string }> = {
  // 16px box
  sm: {
    box: 'h-4 w-4',
    icon: 'h-2.5 w-2.5',
  },
  // 20px box
  md: {
    box: 'h-5 w-5',
    icon: 'h-3 w-3',
  },
};

// ─── Checkbox ─────────────────────────────────────────────────────────────────

export function Checkbox({
  checked = false,
  indeterminate = false,
  onChange,
  label,
  disabled = false,
  error,
  size = 'md',
  id: idProp,
  className,
  'aria-label': ariaLabel,
}: CheckboxProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const errorId = error ? `${id}-error` : undefined;

  const inputRef = useRef<HTMLInputElement>(null);

  // Sync indeterminate DOM property — not settable via HTML attribute
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate && !checked;
    }
  }, [indeterminate, checked]);

  const s = SIZE_CLASSES[size];

  const isChecked = checked || indeterminate;

  const boxClasses = [
    'shrink-0 rounded border-2 flex items-center justify-center transition-colors duration-150',
    s.box,
    // Mobile: 44×44 touch target via padding on wrapper; box itself stays sized
    isChecked
      ? 'bg-brand-500 border-brand-500'
      : error
        ? 'bg-white dark:bg-neutral-900 border-danger-500'
        : 'bg-white dark:bg-neutral-900 border-neutral-300 dark:border-neutral-600',
    disabled
      ? 'opacity-50 cursor-not-allowed bg-neutral-200 dark:bg-neutral-700 border-neutral-400 dark:border-neutral-500'
      : !isChecked && !error
        ? 'hover:border-brand-500'
        : '',
  ]
    .filter(Boolean)
    .join(' ');

  const labelClasses = [
    'text-sm font-medium leading-5 select-none',
    disabled
      ? 'text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
      : 'text-neutral-700 dark:text-neutral-300 cursor-pointer',
  ].join(' ');

  return (
    <div className={className}>
      {/* Wrapper with 44×44 touch target on mobile */}
      <div className="flex items-start gap-2">
        <span className="relative flex items-center justify-center p-[2px] sm:p-0 -m-[2px] sm:m-0 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0">
          <input
            ref={inputRef}
            type="checkbox"
            id={id}
            checked={checked}
            disabled={disabled}
            aria-checked={indeterminate && !checked ? 'mixed' : checked}
            aria-label={ariaLabel}
            aria-describedby={errorId}
            onChange={(e) => {
              if (!disabled) onChange?.(e.target.checked);
            }}
            className="absolute inset-0 m-0 h-full w-full cursor-pointer opacity-0 peer disabled:cursor-not-allowed"
          />
          {/* Visual box — click delegated via label */}
          <label
            htmlFor={id}
            aria-hidden="true"
            className={boxClasses}
          >
            {/* Checkmark icon */}
            {checked && !indeterminate && (
              <svg
                className={s.icon}
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M2 6L5 9L10 3"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            {/* Indeterminate dash */}
            {indeterminate && !checked && (
              <svg
                className={s.icon}
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M2 6H10"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </label>
          {/* Focus ring overlay */}
          <span className="absolute inset-0 rounded peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 peer-focus-visible:ring-offset-2 pointer-events-none" />
        </span>

        {label && (
          <label htmlFor={id} className={labelClasses}>
            {label}
          </label>
        )}
      </div>

      {error && (
        <p id={errorId} className="mt-1 text-xs text-danger-500">
          {error}
        </p>
      )}
    </div>
  );
}
